import React, { useEffect, useMemo, useState } from "react";
import { Container, Spacer, VDBText } from "@components/atoms";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import styled, { css, useTheme, DefaultTheme } from "styled-components/native";
import {
  PairShareOption,
  ShareProps,
  ShareScreenProps,
  ShareType,
} from "./types";
import Modal from "@components/atoms/Modal";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import MobileKeyboardAwareSlideSheet from "@components/atoms/MobileKeyboardAwareSlideSheet";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { schema } from "./utils/shareSchema";
import { useShare, useShareableLink } from "@apis/share";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useTranslation } from "react-i18next";
import {
  mapOptionKeysToNames,
  ShareVia,
  useItemInfo,
  useProcessItemMedia,
} from "./utils/utils";
import ShareShortTitle from "./components/ShareShortTitle";
import ShareOptions from "./components/ShareOption";
import ShareCheckBoxSection from "./components/ShareCheckBoxSelections";
import PairShareSelection from "./components/PairShareSelection";
import { Diamond } from "@src/types/DiamondDetail";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { Dimensions, Platform, Pressable } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import ShareButton from "./utils/ShareButton";
import OrderCartItemComponent from "@components/molecules/OrderCartItemCard/index";
import { ProductCategory } from "@utils/constants";
import { SelectedItemsProvider } from "../../../contexts/SelectedItemsContext";
import {
  useShareOptionPriceDisclaimer,
  useEnablePairShareOptions,
} from "@redux/slices/globalSlice";
import PriceDisclaimer from "@scenes/pdp/utils/PriceDisclaimer";
import { useSelector } from "react-redux";
import { isLinkedPair } from "@utils/product/productValidators";
import { getToastBottomMargin } from "@scenes/searchResults/components/utils";

let Share: any =
  Platform.OS !== "web" ? require("react-native-share").default : undefined;

const ShareScreen = ({
  mItem,
  onClose,
  shareType,
  bulkItems,
  collection,
  favoriteIds,
  favoriteCount,
  selectedOption,
  fromReviewPage,
  stoneItemDetail,
  matchingPairItemDetail,
  jcSelectedOption,
  personalization,
}: ShareProps) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const toastBottomMargin = getToastBottomMargin(0, insets.bottom);
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [isVideoChecked, setIsVideoChecked] = useState(true);
  const [priceChecked, setPriceChecked] = useState(true);
  const shareOptionPriceDisclaimer = useShareOptionPriceDisclaimer();
  const [checkedItems, setCheckedItems] = useState({
    images: {},
    reports: {},
    videos: false,
  });

  const [pairShareOption, setPairShareOption] = useState<PairShareOption>(
    PairShareOption.PAIR
  );
  const enablePairShareOptions = useEnablePairShareOptions();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);

  const showPairShareOptions = useMemo(() => {
    return (
      isCustomApp &&
      enablePairShareOptions &&
      isLinkedPair(mItem) &&
      shareType === ShareType.singleShare
    );
  }, [isCustomApp, enablePairShareOptions, mItem, shareType]);

  const effectiveItem = useMemo(() => {
    if (!showPairShareOptions || pairShareOption === PairShareOption.PAIR) {
      return mItem;
    }
    const children = mItem?.children ?? [];
    if (pairShareOption === PairShareOption.STONE_1 && children.length >= 1) {
      return children[0];
    }
    if (pairShareOption === PairShareOption.STONE_2 && children.length >= 2) {
      return children[1];
    }
    return mItem;
  }, [showPairShareOptions, pairShareOption, mItem]);

  const config = mapOptionKeysToNames(
    mItem?.display_configuration_v2,
    jcSelectedOption?.current?.option_value,
    jcSelectedOption?.current?.option_visible,
    fromReviewPage
  );
  const isImageChecked = Object.values(checkedItems.images)?.some((val) => val);
  const isReportsChecked = Object.values(checkedItems.reports)?.some(
    (val) => val
  );
  const [bulkShareChecked, setBulkShareChecked] = useState({
    bulkImageShare: true,
    bulkReportsShare: true,
  });

  const { control, getValues, handleSubmit } = useForm({
    resolver: yupResolver(schema(t)),
  });
  const { bulkItemIds, itemCount, totalSalesPrice } = useItemInfo(
    bulkItems as Diamond[] | undefined
  );
  const { trackButtonEvent } = useUserAnalytics();
  const shareItem = useShare();
  const shareableLink = useShareableLink();
  const { sortedImageItems, sortedVideoItems, sortedReports, itemMediaData } =
    useProcessItemMedia(
      fromReviewPage
        ? [
            mItem?.id?.toString(),
            stoneItemDetail?.id?.toString(),
            ...(matchingPairItemDetail
              ? [matchingPairItemDetail.id?.toString()]
              : []),
          ]
        : [effectiveItem?.id?.toString()]
    );

  useEffect(() => {
    if (itemMediaData) {
      setCheckedItems({
        images: sortedImageItems.reduce(
          (id: any, item: { order: any }) => ({ ...id, [item.order]: true }),
          {}
        ),
        reports: sortedReports.reduce(
          (id: any, item: { order: any }) => ({ ...id, [item.order]: true }),
          {}
        ),
        videos:
          shareType === ShareType.singleShare
            ? sortedVideoItems.length > 0
              ? true
              : false
            : true,
      });
    }
  }, [itemMediaData]);

  const emailContent = useMemo(() => {
    if (selectedOption !== ShareVia.EMAIL) return null;

    return (
      <>
        <VDBText type={typography["H1-B"]} color={theme["core-05"]}>
          {t("shareScreen.screenText.email.text")}
        </VDBText>
        <Spacer size={SpaceType.Double} />

        <ShareOptions control={control} selectedOption={selectedOption} />

        <Spacer size={SpaceType.ExtraLarge} />
      </>
    );
  }, [selectedOption]);

  return (
    <>
      <Container>
        <Spacer size={SpaceType.Double} />
        {showPairShareOptions && (
          <PairShareSelection
            item={mItem}
            selectedOption={pairShareOption}
            onOptionChange={setPairShareOption}
          />
        )}
        {!fromReviewPage && (
          <ShareShortTitle
            shareType={shareType}
            userInfo={userInfo.user}
            collection={collection}
            favoriteCount={favoriteCount}
            mItem={effectiveItem}
            itemCount={itemCount}
            totalSalesPrice={totalSalesPrice}
          />
        )}
        {shareOptionPriceDisclaimer?.price_modes?.includes(
          userInfo?.getPriceMode()
        ) && (
          <>
            <Spacer size={SpaceType.Basic} />
            <PriceDisclaimer
              key={"SHARE_PRICE_DISCLAIMER"}
              fromSharePage={true}
              config={shareOptionPriceDisclaimer}
              priceMode={userInfo?.getPriceMode()}
            />
            <Spacer size={SpaceType.large} />
          </>
        )}
        {fromReviewPage && (
          <>
            <OrderCartItemComponent
              mItem={mItem}
              priceMode={userInfo?.getPriceMode()}
              productType={ProductCategory.JEWELRY}
              showRemoveIcon={false}
              isBuilderItem={true}
              stoneItem1={stoneItemDetail}
              stoneItem2={matchingPairItemDetail}
              onPress={() => {}}
              configDetails={{
                config: config,
                personalization: personalization,
              }}
            />
            <Spacer size={SpaceType.Big} />
          </>
        )}

        {emailContent}

        <VDBText type={typography["H1-B"]} color={theme["core-05"]}>
          {t("shareScreen.screenText.whatToShareV1.text")}
        </VDBText>
        <Spacer size={SpaceType.Double} />
        <ProductContainer>
          {(selectedOption === ShareVia.EMAIL ||
            selectedOption === ShareVia.NATIVE) && (
            <ViewContainer>
              <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
                {t("shareScreen.screenText.discription.text")}
              </VDBText>
              <Spacer size={SpaceType.Basic} />
              <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
                {t("shareScreen.screenText.discription.inlineText.text")}
              </VDBText>
            </ViewContainer>
          )}

          <ShareCheckBoxSection
            shareType={shareType}
            reports={sortedReports}
            videoItems={sortedVideoItems}
            imageItems={sortedImageItems}
            userInfo={userInfo.user}
            priceChecked={priceChecked}
            setPriceChecked={setPriceChecked}
            checkedItems={checkedItems}
            isVideoChecked={isVideoChecked}
            setIsVideoChecked={setIsVideoChecked}
            checkedReports={checkedItems.reports}
            setCheckedItems={setCheckedItems}
            setBulkShareChecked={setBulkShareChecked}
            bulkShareChecked={bulkShareChecked}
            itemDetail={mItem}
          />
        </ProductContainer>
      </Container>
      <Spacer size={SpaceType.large} />
      <ButtonContainer insets={insets}>
        <ContainerOverlay>
          {ShareButton(
            handleSubmit,
            selectedOption,
            shareType,
            userInfo,
            favoriteIds,
            effectiveItem,
            stoneItemDetail,
            bulkItemIds,
            isImageChecked,
            sortedImageItems,
            isReportsChecked,
            sortedReports,
            checkedItems,
            sortedVideoItems,
            priceChecked,
            collection,
            bulkShareChecked,
            itemCount,
            favoriteCount,
            shareableLink,
            shareItem,
            onClose,
            Share,
            trackButtonEvent,
            getValues(),
            jcSelectedOption,
            personalization,
            fromReviewPage,
            matchingPairItemDetail,
            toastBottomMargin
          )}
        </ContainerOverlay>
      </ButtonContainer>
    </>
  );
};
const ShareModal = ({
  isOpen,
  onClose,
  item,
  shareType,
  bulkItems,
  collection,
  favoriteIds,
  favoriteCount,
  selectedOption,
  fromReviewPage,
  stoneItemDetail,
  matchingPairItemDetail,
  jcSelectedOption,
  personalization,
  title,
  inWindow,
}: ShareScreenProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const typography = useTypographyStore();
  const insets = useSafeAreaInsets();

  const shareScreenContent = (
    <ShareScreen
      onClose={onClose}
      mItem={item}
      shareType={shareType}
      bulkItems={bulkItems ?? []}
      collection={collection}
      favoriteIds={favoriteIds}
      favoriteCount={favoriteCount}
      selectedOption={selectedOption}
      fromReviewPage={fromReviewPage}
      stoneItemDetail={stoneItemDetail}
      matchingPairItemDetail={matchingPairItemDetail}
      jcSelectedOption={jcSelectedOption}
      personalization={personalization}
    />
  );

  const headerTitle =
    selectedOption === ShareVia.LINK
      ? t("shareScreen.title.link", "Share a Link")
      : title
      ? title
      : t("shareScreen.title.text");

  if (inWindow) {
    if (!isOpen) {
      return null;
    }
    return (
      <SelectedItemsProvider>
        <InWindowOverlayRoot style={{ paddingBottom: insets.bottom }}>
          <InWindowBackdrop onPress={onClose} />
          <InWindowSheet>
            <InWindowHeaderStrip>
              <VDBText
                textAlign="center"
                type={typography["H2-M"]}
                color={theme["core-05"]}
              >
                {headerTitle}
              </VDBText>
              <InWindowCloseButton
                testID="share-modal-in-window-close"
                onPress={onClose}
              >
                <VDBIcon
                  name={VDBIcons.CLOSE_CIRCLE}
                  variant={IconsVariant.VDBCustom}
                  size={24}
                  color={theme["core-06"]}
                />
              </InWindowCloseButton>
            </InWindowHeaderStrip>
            <InWindowDivider />
            <InWindowBody>
              <KeyboardAwareScrollView
                bottomOffset={20}
                keyboardShouldPersistTaps="always"
                showsVerticalScrollIndicator={false}
              >
                {shareScreenContent}
              </KeyboardAwareScrollView>
            </InWindowBody>
          </InWindowSheet>
        </InWindowOverlayRoot>
      </SelectedItemsProvider>
    );
  }

  const customSheetForWebMobileTablet =
    Platform.OS === "web" && (theme.isMobile || theme.isTablet);

  if (customSheetForWebMobileTablet) {
    return (
      <MobileKeyboardAwareSlideSheet
        isOpen={isOpen}
        onClose={onClose}
        headerTitle={title ? title : t("shareScreen.title.text")}
        backgroundColor={theme["core-02"]}
      >
        {shareScreenContent}
      </MobileKeyboardAwareSlideSheet>
    );
  }

  return (
    <Modal
      headerTitle={headerTitle}
      isOpen={isOpen}
      onClose={onClose}
      backgroundColor={theme["core-02"]}
      size={576}
    >
      <SelectedItemsProvider>{shareScreenContent}</SelectedItemsProvider>
    </Modal>
  );
};

const InWindowOverlayRoot = styled.View`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  z-index: 500;
  elevation: 40;
  justify-content: flex-end;
`;

const InWindowBackdrop = styled(Pressable)`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const IN_WINDOW_MAX_HEIGHT = Math.round(Dimensions.get("window").height * 0.9);

const InWindowSheet = styled.View`
  max-height: ${IN_WINDOW_MAX_HEIGHT}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  overflow: hidden;
`;

const InWindowHeaderStrip = styled.View`
  position: relative;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 16px;
`;

const InWindowDivider = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const InWindowBody = styled.View`
  flex: 1;
  flex-basis: auto;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
`;

const InWindowCloseButton = styled.TouchableOpacity`
  position: absolute;
  right: 12px;
`;

const ButtonContainer = styled.View<{
  itemType?: any;
  insets?: { bottom: number };
}>`
  padding-top: ${SpaceType.Big}px;
  padding-bottom: ${({ theme, insets }) =>
    !theme.isMobile
      ? `${SpaceType.ExtraLarge}px`
      : `${
          SpaceType.ExtraLarge +
          (Platform.OS === "android"
            ? insets && insets.bottom > 0
              ? insets.bottom
              : SpaceType.Double
            : 0)
        }px`};
  align-items: center;
  padding-horizontal: ${SpaceType.Double}px;
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      padding-horizontal: 0px;
    `}
`;

const ProductContainer = styled.View`
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const ViewContainer = styled.View`
  padding: ${SpaceType.Double}px;
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }) =>
    !theme.isMobile &&
    css`
      max-width: 343px;
    `}
`;

export default ShareModal;
